<template>
  <div id="rg-date-input-dialog">
    <vs-prompt
      class="rg-date-input"
      title="再生履歴の日時設定"
      accept-text="設定"
      cancel-text="キャンセル"
      :active="active"
      is-valid="true"
      @accept="onAccept"
      @cancel="onCancel"
    >
      <div class="input-form">
        <div class="date-time-input">
          <v-date-picker
            v-model="inputDatetime"
            :max-date="maxDate"
            :minute-increment="30"
            mode="dateTime"
            :masks="masks"
            is-required
            is-expanded
            is24hr
            :valid-hours="validHours"
          />
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { createNamespacedHelpers } from "vuex";
import C from "../Constants";
import range from "lodash/range";

const EMIT_EVENTS = {
  CANCEL: "cancel",
};

const {
  mapGetters: mapGettersOfRGPlayback,
  mapMutations: mapMutationsOfRGPlayback,
} = createNamespacedHelpers("radarGridPlayback");

/**
 * オフセットを引数として受け取り、入力日時を計算する関数を生成
 *
 * @param {number} offsetHours - オフセット値（デフォルトはC.PLAYBACK_DATE_OFFSET）
 * @returns {Function} - 入力日時を計算する関数
 */
const calcInputDateTimeCreator = (offsetHours = C.PLAYBACK_DATE_OFFSET) => {
  const momentNow = moment(); // calcInputDateTimeCreatorの呼び出し時の日時を基準にする
  return () => {
    return moment({
      year: momentNow.year(),
      month: momentNow.month(),
      day: momentNow.date(),
      hour: momentNow.hours(),
      minute: momentNow.minutes(),
    })
      .subtract(offsetHours, "hours")
      .toDate();
  };
};
let calcInputDateTime = calcInputDateTimeCreator();

/**
 * 指定された日付を30分刻みの時間に変換
 * @param {Date} date - 変換する日付
 * @returns {Date|null} - 30分刻みに変換された日付を返す。引数が日付でない場合はnullを返す。
 */
const convertTo30MinuteIncrementsOfTime = (
  date // Date
) => {
  if (!(date instanceof Date)) return null;

  const momentDate = moment(date);
  const INCREMENTS_MINUTE = 30;
  const minutes = momentDate.minutes();
  if (minutes < INCREMENTS_MINUTE) {
    momentDate.startOf("hour").toDate();
  }
  return momentDate.toDate();
};

export default {
  name: "RgDateInputDialog",
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxDate: calcInputDateTime(),
      inputDatetime: convertTo30MinuteIncrementsOfTime(calcInputDateTime()),
      masks: {
        title: "YYYY年 MMM",
      },
    };
  },
  watch: {
    active(val) {
      if (val) {
        const baseTime = this.playbackSettings.baseTime;
        calcInputDateTime = calcInputDateTimeCreator();
        this.maxDate = calcInputDateTime(); // ダイアログがアクティブになった時にmaxDateを更新
        this.inputDatetime = baseTime
          ? new Date(baseTime * 1000)
          : convertTo30MinuteIncrementsOfTime(calcInputDateTime());
      }
    },
    isInputDatetimeSameOrAfterMaxDatetime(val) {
      if (val) {
        this.$nextTick(() => {
          // inputDatetimeが最大日時と同じかそれ以降の場合は、計算し直す
          this.inputDatetime = convertTo30MinuteIncrementsOfTime(
            calcInputDateTime()
          );
        });
      }
    },
  },
  computed: {
    ...mapGettersOfRGPlayback(["playbackSettings"]),
    /**
     * 選択可能な時間の範囲を返す
     * inputDatetimeが最大日の場合、0からinputDatetimeの時間までのintのArrayを返す
     * それ以外の場合、0から23までのintのArrayを返す
     *
     * @returns {number[]} 選択可能な時間(int)のArray
     */
    validHours() {
      if (this.isInputDatetimeMaxDay) {
        return range(0, Math.abs(calcInputDateTime().getHours() + 1));
      }
      return range(0, 23 + 1);
    },
    isInputDatetimeMaxDay() {
      return moment(this.inputDatetime).isSame(this.maxDate, "day");
    },
    isInputDatetimeSameOrAfterMaxDatetime() {
      return moment(this.inputDatetime).isSameOrAfter(this.maxDate, "minute");
    },
  },
  methods: {
    ...mapMutationsOfRGPlayback(["SET_IS_ACTIVE_DATE_INPUT_DIALOG"]),
    onAccept() {
      setTimeout(() => {
        this.$emit("sendInputValue", this.inputDatetime);
        this.SET_IS_ACTIVE_DATE_INPUT_DIALOG(false);
      });
    },
    onCancel() {
      setTimeout(() => {
        this.$emit(EMIT_EVENTS.CANCEL);
        this.SET_IS_ACTIVE_DATE_INPUT_DIALOG(false);
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

.rg-date-input {
  .vs-con-input-label {
    width: inherit;
  }

  .rg-date-time-input-notes {
    margin-bottom: 20px;
  }

  .vs-dialog-cancel {
    display: none;
  }
}
</style>
